import isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { injectBasePath } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@legacy/HTMLRenderEngine';
import { withAsyncErrorHandler } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/error-util';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import WithQuery from '@monorepo/shared/src/components/common/WithQuery';
import { BookType } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import fetchAsText from 'util/fetch-as-text-util';

type Props = {
  bookType: string;
  fromRoute?: string;
};

const LetterBooksContent: React.FC<Props> = ({ bookType, fromRoute }) => {
  const location = useLocation();
  const [{ books, loading: loadingLetterBooks, error }] = usePageStore(bookType as BookType);
  const [loadingBookCovers, setLoadingBookCovers] = useState(true);
  const [bookCovers, setBookCovers] = useState<
    { html: string; id: number; routeParam: string; title: string }[]
  >([]);
  const classes = styles();

  const getCover = (book) => book?.pages.find(({ pageNumber }) => isNil(pageNumber));

  const fetchBookCoversHtml = async (books) => {
    const bookCoversHtmlText = await withAsyncErrorHandler(() =>
      Promise.allSettled(books.map((book) => fetchAsText(getCover(book).src)))
    );

    setBookCovers(() =>
      bookCoversHtmlText
        .map((promise, idx) => ({
          html:
            promise.status === 'fulfilled'
              ? injectBasePath(
                  promise.value.data,
                  getCover(books[idx]).src.slice(0, getCover(books[idx]).src.lastIndexOf('/'))
                )
              : undefined,
          id: books[idx].id,
          routeParam: books[idx].bookCode,
          title: books[idx].title,
        }))
        .filter((promise) => promise.html)
    );

    setLoadingBookCovers(() => false);
  };

  useEffect(() => {
    if (!loadingLetterBooks && books.length) {
      fetchBookCoversHtml(books);
    }
  }, [loadingLetterBooks, books]);

  return (
    <WithQuery
      loading={loadingLetterBooks || loadingBookCovers}
      error={error || (!loadingBookCovers && !bookCovers.length)}
    >
      <Grid container spacing={6} className={classes.gridContainer}>
        {bookCovers.map((bookCovers) => (
          <Grid
            key={bookCovers.id}
            className={classes.gridItem}
            item
            xs={12}
            sm={12}
            md={6}
            lg={3}
            xl={3}
          >
            <Link
              to={{
                pathname: `${location.pathname}/${bookCovers.routeParam}`,
              }}
              state={{
                title: bookCovers.title,
                from: fromRoute,
              }}
            >
              <div className={classes.iframeContainer}>
                <iframe
                  frameBorder={0}
                  className={classes.cover}
                  scrolling="no"
                  srcDoc={bookCovers.html}
                  title={bookCovers.title}
                ></iframe>
                <div className={classes.iframeOverlay}></div>
              </div>
            </Link>
          </Grid>
        ))}
      </Grid>
    </WithQuery>
  );
};

const styles = makeStyles(() => ({
  cover: {
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    height: '400px',
    width: 'auto',
  },
  gridItem: {
    textAlign: 'center',
  },
  gridContainer: {
    padding: '4rem',
    overflow: 'hidden',
    width: '100%',
  },
  iframeContainer: {
    position: 'relative',
  },
  iframeOverlay: {
    height: '100%',
    left: '0',
    position: 'absolute',
    top: '0',
    width: '100%',
  },
}));

export default LetterBooksContent;
